import { User, Bot, Copy, Check } from 'lucide-react';
import { useState } from 'react';
import { formatInTz } from '../../utils/dateFormatters';
import { useTimezone } from '../../hooks/useTimezone';
import { parseMarkdown } from '../../utils/markdownRenderer';

interface ChatMessageProps {
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
  userAvatarUrl?: string | null;
}

export function ChatMessage({ role, content, timestamp, userAvatarUrl }: ChatMessageProps) {
  const tz = useTimezone();
  const [copied, setCopied] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [userImageError, setUserImageError] = useState(false);
  const isUser = role === 'user';

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <div className={`flex gap-3 ${isUser ? 'flex-row-reverse' : 'flex-row'} mb-6 group`}>
      <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center shadow-md overflow-hidden ${
        isUser ? 'bg-[#1875FF]' : 'bg-gradient-to-br from-[#1875FF] to-[#05E0D1]'
      }`}>
        {isUser ? (
          userAvatarUrl && !userImageError ? (
            <img
              src={userAvatarUrl}
              alt="You"
              className="w-full h-full object-cover"
              onError={() => setUserImageError(true)}
            />
          ) : (
            <User className="w-5 h-5 text-white" />
          )
        ) : !imageError ? (
          <img
            src="https://utimppxbdeptpoyotmcx.supabase.co/storage/v1/object/public/agents-avatars/550e8400-e29b-41d4-a716-446655440001/sales-coach.png"
            alt="Sales Coach"
            className="w-full h-full object-cover"
            onError={() => {
              console.warn('Failed to load Sales Coach avatar');
              setImageError(true);
            }}
            loading="eager"
          />
        ) : (
          <Bot className="w-5 h-5 text-white" />
        )}
      </div>

      <div className={`flex-1 max-w-3xl ${isUser ? 'flex flex-col items-end' : ''}`}>
        <div className={`rounded-2xl shadow-lg ${
          isUser
            ? 'px-4 py-3 bg-gradient-to-br from-[#1875FF] to-[#1875FF]/90 text-white'
            : 'px-5 py-4 bg-gradient-to-br from-white to-[#1875FF]/5 text-gray-900 border-2 border-[#1875FF]/20'
        }`}>
          {isUser ? (
            <div className="whitespace-pre-wrap break-words font-medium">{content}</div>
          ) : (
            <div className="text-gray-900 leading-relaxed">
              {parseMarkdown(content)}
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 mt-1 px-2">
          <span className="text-xs text-gray-600 font-medium">
            {formatInTz(new Date(timestamp), 'MMM d, h:mm a', tz)}
          </span>

          {!isUser && (
            <button
              onClick={copyToClipboard}
              className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-[#1875FF]/10 rounded"
              title="Copy message"
            >
              {copied ? (
                <Check className="w-3 h-3 text-[#05E0D1]" />
              ) : (
                <Copy className="w-3 h-3 text-gray-600" />
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
